import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
	const heroRef = useRef(null);
	const carouselRef = useRef(null);
	const carouselInterval = useRef(null);
	const activeIndex = useRef(0);
	const [currentTab, setCurrentTab] = useState("Tab 1");
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

	const carouselTexts = [
		"Sol Treasury Corp is a subsidiary owned 100% by Acheter-Louer, which buys, holds and stakes SOL.",
		"Acheter-Louer is listed on Euronext Growth Paris (ALALO).",
		"It is the most effective way to accumulate SOL."
	];

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth <= 767);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		// Configuration immédiate sans timeout
		const firstContent = document.querySelectorAll('[data-hero-anim-content-1="true"]');
		const secondContent = document.querySelector('[data-hero-anim-content-2="true"]');
		
		// Styles initiaux pour le premier contenu - visible immédiatement
		firstContent.forEach(element => {
			element.style.opacity = '1';
			element.style.visibility = 'visible';
		});
		
		// Styles initiaux pour le deuxième contenu
		if (secondContent) {
			secondContent.style.cssText = `
				position: fixed !important;
				visibility: hidden !important;
				opacity: 0 !important;
				top: 50% !important;
				left: 50% !important;
				transform: translate(-50%, -50%) !important;
				width: 90% !important;
				max-width: 1200px !important;
				z-index: 1000 !important;
				pointer-events: none !important;
			`;
		}

		// Configuration du scroll trigger avec des phases plus rapides
		ScrollTrigger.create({
			trigger: heroRef.current,
			start: "top top",
			end: "+=200vh",
			pin: true,
			pinType: "fixed",
			pinSpacing: false,
			scrub: 0.5,
			anticipatePin: 1,
			onUpdate: (self) => {
				const progress = self.progress;
				
				// Phase 1 (0-15%) : Premier contenu visible
				if (progress < 0.15) {
					firstContent.forEach(element => {
						gsap.set(element, {
							opacity: 1,
							visibility: 'visible',
							force3D: true
						});
					});
					
					if (secondContent) {
						gsap.set(secondContent, {
							visibility: 'hidden',
							opacity: 0,
							position: 'fixed',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							pointerEvents: 'none',
							force3D: true
						});
					}
				} 
				// Phase 2 (15-35%) : Disparition rapide du premier contenu
				else if (progress >= 0.15 && progress <= 0.35) {
					const transitionProgress = (progress - 0.15) / 0.2;
					
					firstContent.forEach(element => {
						gsap.set(element, {
							opacity: 1 - transitionProgress,
							visibility: 'visible',
							force3D: true
						});
					});
					
					if (secondContent) {
						gsap.set(secondContent, {
							visibility: 'hidden',
							opacity: 0,
							position: 'fixed',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							pointerEvents: 'none',
							force3D: true
						});
					}
				}
				// Phase 3 (35-55%) : Écran vide plus court
				else if (progress > 0.35 && progress <= 0.55) {
					firstContent.forEach(element => {
						gsap.set(element, {
							opacity: 0,
							visibility: 'hidden',
							force3D: true
						});
					});
					
					if (secondContent) {
						gsap.set(secondContent, {
							visibility: 'hidden',
							opacity: 0,
							position: 'fixed',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							pointerEvents: 'none',
							force3D: true
						});
					}
				}
				// Phase 4 (55-100%) : Apparition rapide du deuxième contenu
				else {
					const finalProgress = (progress - 0.55) / 0.45;
					
					firstContent.forEach(element => {
						gsap.set(element, {
							opacity: 0,
							visibility: 'hidden',
							force3D: true
						});
					});
					
					if (secondContent) {
						gsap.set(secondContent, {
							position: 'fixed',
							visibility: 'visible',
							opacity: finalProgress,
							top: '45%', // Remonté de 50% à 45% pour mieux centrer
							left: '50%',
							transform: 'translate(-50%, -50%)',
							width: '90%',
							maxWidth: '1200px',
							zIndex: 1000,
							pointerEvents: finalProgress > 0.3 ? 'auto' : 'none',
							force3D: true
						});
					}
				}
			}
		});

		// Configuration du carrousel
		const carouselLines = document.querySelectorAll('.carrousel-line-trigger');
		
		const updateCarousel = (index) => {
			carouselLines.forEach(line => {
				line.classList.remove('is-active');
				const carouselLine = line.querySelector('.carrousel-line');
				if (carouselLine) {
					gsap.to(carouselLine, { width: '0%', duration: 0.3 });
				}
			});

			if (carouselLines[index]) {
				carouselLines[index].classList.add('is-active');
				const activeLine = carouselLines[index].querySelector('.carrousel-line');
				if (activeLine) {
					gsap.to(activeLine, { width: '100%', duration: 10 });
				}
			}

			const carouselText = document.querySelector('[data-carrousel-text="true"]');
			if (carouselText) {
				carouselText.textContent = carouselTexts[index];
			}
		};

		updateCarousel(2);

		carouselInterval.current = setInterval(() => {
			activeIndex.current = (activeIndex.current + 1) % carouselTexts.length;
			updateCarousel(activeIndex.current);
		}, 10000);

		return () => {
			if (carouselInterval.current) {
				clearInterval(carouselInterval.current);
			}
			ScrollTrigger.getAll().forEach(trigger => trigger.kill());
		};
	}, []);

	const handleTabClick = (e, tabName) => {
		e.preventDefault();
		setCurrentTab(tabName);
	};

	return (
		<section 
			className="section_hero" 
			ref={heroRef} 
			style={{ 
				position: 'relative', 
				height: '100vh', 
				overflow: 'hidden',
				willChange: 'transform'
			}}
		>
			<div className="padding-global is-100">
				<div className="container-large is-100">
					<div 
						className="hero-sticky_wrapper" 
						style={{ 
							position: 'relative', 
							height: '100vh', 
							display: 'flex', 
							alignItems: 'center', 
							justifyContent: 'center',
							willChange: 'transform'
						}}
					>
						<div className="hero-wrapper" style={{ width: '100%', textAlign: 'center', position: 'relative' }}>
							<div className="hero-spacer"></div>
							
							{/* Premier contenu - titre modifié avec Sol Treasury Corp en gros */}
							<div 
								data-hero-anim-content-1="true" 
								className="home-hero-title_wrapper" 
								style={{ 
									opacity: 1, 
									marginBottom: '2rem', 
									visibility: 'visible',
									transform: 'translate3d(0,0,0)'
								}}
							>
								<div className="align-center">
									<div className="max-width-59" style={{ position: 'relative' }}>
										<div className="text-align-center">
											<h1 
												className="text-weight-medium"
												style={{
													fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
													fontWeight: 'bold',
													marginBottom: '1rem',
													lineHeight: '1.1'
												}}
											>
												Sol Treasury Corp
											</h1>
											
											{/* Logo Acheter-Louer en bas à droite du titre */}
											<div 
												style={{
													position: 'absolute',
													bottom: 'clamp(55px, 8vw, 50px)',
													...(isMobile ? {
														left: '50%',
														right: 'auto',
														transform: 'translateX(-50%)'
													} : {
														right: 'clamp(-40px, -8vw, -80px)'
													}),
													display: 'flex',
													alignItems: 'center',
													gap: 'clamp(4px, 1vw, 8px)',
													fontSize: '0.9rem',
													color: '#888',
													fontWeight: 'normal'
												}}
											>
												<span>by</span>
												<img 
													src="/logo-htl.png" 
													alt="Acheter-Louer" 
													style={{
														height: 'clamp(20px, 5vw, 32px)',
														width: 'auto',
														opacity: 1,
														backgroundColor: 'white',
														borderRadius: '2px',
														padding: '2px 4px'
													}}
												/>
											</div>
											
											<h2 
												style={{
													fontSize: 'clamp(1.2rem, 4vw, 2rem)',
													fontWeight: 'normal',
													color: '#cccccc',
													marginTop: '2rem',
													lineHeight: '1.3'
												}}
											>
												The First European Solana Treasury Company
											</h2>
										</div>
									</div>
								</div>
							</div>
							
							<div
								data-hero-anim="true"
								data-hero-anim-content-1="true"
								className="home-carrousel-button_wrapper"
								style={{ 
									opacity: 1, 
									visibility: 'visible',
									transform: 'translate3d(0,0,0)'
								}}
							>
								<div className="carrousel_wrapper" ref={carouselRef}>
									<div className="carrousel-lines_wrapper">
										{carouselTexts.map((text, index) => (
											<div 
												key={index} 
												data-tab-text={text} 
												className={`carrousel-line-trigger ${index === 2 ? 'is-active' : ''}`}
											>
												<div className="carrousel-base-line">
													<div 
														className="carrousel-line" 
														style={{ width: index === 2 ? '27.46%' : '0%' }}
													></div>
												</div>
											</div>
										))}
									</div>
									<div className="carrousel-content_wrapper">
										<div className="carrousel-tab_wrapper">
											<div className="max-width-39">
												<div className="text-size-large">
													<div data-carrousel-text="true">
														It is the most effective way to accumulate SOL.
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div 
									data-hero-anim-content-1="true"
									className="button-group is-center" 
									style={{ 
										marginTop: '2rem', 
										opacity: 1, 
										visibility: 'visible',
										transform: 'translate3d(0,0,0)'
									}}
								>
									<a href="#" className="about-button w-inline-block">
										<div>About Us</div>
										<div className="button-arrow w-embed">
											<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
												<g clipPath="url(#clip0_4068_785)">
													<path d="M13.3337 7.99996L12.3937 7.05996L8.66699 10.78V2.66663H7.33366V10.78L3.61366 7.05329L2.66699 7.99996L8.00033 13.3333L13.3337 7.99996Z" fill="#FAFAFA"></path>
												</g>
												<defs>
													<clipPath id="clip0_4068_785">
														<rect width="16" height="16" fill="white"></rect>
													</clipPath>
												</defs>
											</svg>
										</div>
									</a>
								</div>
							</div>

							{/* Deuxième contenu - repositionné plus haut */}
							<div
								data-hero-anim-content-2="true"
								className="hero_wrapper-second-part"
								style={{ 
									position: 'fixed',
									visibility: 'hidden',
									opacity: 0,
									top: '45%', // Remonté de 50% à 45%
									left: '50%',
									transform: 'translate(-50%, -50%)',
									width: '90%',
									maxWidth: '1000px', // Réduit la largeur max
									zIndex: 1000,
									pointerEvents: 'none',
									willChange: 'transform, opacity'
								}}
							>
								<div
									data-current={currentTab}
									data-easing="ease"
									data-duration-in="300"
									data-duration-out="100"
									className="hero-tabs w-tabs"
								>
									<div className="hero-tabs-content w-tab-content">
										<div
											data-w-tab="Tab 1"
											className={`hero-tab-pane w-tab-pane ${currentTab === "Tab 1" ? 'w--tab-active' : ''}`}
											id="w-tabs-0-data-w-pane-0"
											role="tabpanel"
											aria-labelledby="w-tabs-0-data-w-tab-0"
										>
											<div className="hero-tab-pane-content">
												<div className="max-width-80">
													<div className="text-align-center">
														<div 
															className="heading-style-h1"
															style={{ 
																fontSize: 'clamp(1.4rem, 4vw, 3.5rem)', // Adaptation fluide de 1.4rem à 3.5rem
																lineHeight: 'clamp(1.4, 1.2, 1.2)', // Line-height adaptatif
																marginBottom: '2rem',
																padding: '0 clamp(0.5rem, 2vw, 1rem)', // Padding adaptatif
																wordBreak: 'break-word', // Évite les débordements de texte
																hyphens: 'auto' 
															}}
														>
															Acheter-Louer is an online real estate portal
															that has strategically repositioned itself
															into a Solana treasury company via its
															fully-owned subsidiary Sol Treasury Corp.
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											data-w-tab="Tab 2"
											className={`hero-tab-pane w-tab-pane ${currentTab === "Tab 2" ? 'w--tab-active' : ''}`}
											id="w-tabs-0-data-w-pane-1"
											role="tabpanel"
											aria-labelledby="w-tabs-0-data-w-tab-1"
										>
											<div className="hero-tab-pane-content">
												<div className="max-width-80">
													<div className="text-align-center">
														<div 
															className="heading-style-h1"
															style={{ 
																fontSize: 'clamp(1.4rem, 4vw, 3.5rem)',
																lineHeight: '1.2',
																marginBottom: '2rem'
															}}
														>
															Become Europe's largest Solana treasury
															company with international recognition.
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div 
										className="hero-tabs-menu w-tab-menu" 
										role="tablist"
										style={{ 
											marginTop: '1rem', // Réduit l'espacement
											gap: '1rem'
										}}
									>
										<a
											data-w-tab="Tab 1"
											className={`hero-tab-button w-inline-block w-tab-link ${currentTab === "Tab 1" ? 'w--current' : ''}`}
											id="w-tabs-0-data-w-tab-0"
											href="#w-tabs-0-data-w-pane-0"
											role="tab"
											aria-controls="w-tabs-0-data-w-pane-0"
											aria-selected={currentTab === "Tab 1"}
											onClick={(e) => handleTabClick(e, "Tab 1")}
										>
											<div className="text-size-small">
												<div className="text-style-allcaps">
													<div className="typeface-dm-mono">
														<div>Who we are</div>
													</div>
												</div>
											</div>
										</a>
										<a
											data-w-tab="Tab 2"
											className={`hero-tab-button w-inline-block w-tab-link ${currentTab === "Tab 2" ? 'w--current' : ''}`}
											tabIndex="-1"
											id="w-tabs-0-data-w-tab-1"
											href="#w-tabs-0-data-w-pane-1"
											role="tab"
											aria-controls="w-tabs-0-data-w-pane-1"
											aria-selected={currentTab === "Tab 2"}
											onClick={(e) => handleTabClick(e, "Tab 2")}
										>
											<div className="text-size-small">
												<div className="text-style-allcaps">
													<div className="typeface-dm-mono">
														<div>our mission</div>
													</div>
												</div>
											</div>
										</a>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Hero;